import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, Shuffle, Trophy } from "lucide-react";
import { api, errMsg, fmtDate } from "@/lib/api";
import { PageHead } from "@/pages/admin/AdminLayout";
import { Table } from "@/pages/admin/Payments";

const Stat = ({ k, v, testid }) => (
  <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
    <p className="text-[11px] font-mono-epl uppercase tracking-wider text-slate-500">{k}</p>
    <p data-testid={testid} className="mt-2 font-display font-extrabold text-3xl">{v}</p>
  </div>
);

const download = async () => {
  try {
    const r = await api.get("/admin/giveaway/export.csv", { responseType: "blob" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(r.data);
    a.download = "giveaway-entries.csv";
    a.click();
    URL.revokeObjectURL(a.href);
  } catch (e) {
    toast.error(errMsg(e));
  }
};

const Winners = ({ draws }) => draws.length > 0 && (
  <div data-testid="giveaway-winners" className="mt-6 rounded-2xl border border-[var(--accent)]/30 bg-[var(--accent)]/[0.05] p-5">
    <p className="flex items-center gap-2 font-display font-bold"><Trophy size={18} className="accent-text" />Winners drawn</p>
    {draws.map((d) => (
      <p key={d.id} data-testid={`giveaway-winner-${d.id}`} className="mt-2 text-sm text-slate-200">
        <span className="font-medium">{d.email}</span> <span className="text-slate-400">· drawn {fmtDate(d.drawn_at)} by {d.drawn_by} from {d.pool_size} eligible entries</span>
      </p>
    ))}
  </div>
);

export default function Giveaway() {
  const [d, setD] = useState(null);
  const load = () => api.get("/admin/giveaway").then((r) => setD(r.data));
  useEffect(() => {
    load();
  }, []);
  const draw = async () => {
    if (!window.confirm("Randomly draw a winner from all eligible entries? This is recorded in the audit log.")) return;
    try {
      const { data } = await api.post("/admin/giveaway/draw");
      toast.success(`Winner: ${data.email}`);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const toggle = (e) => api.post(`/admin/giveaway/${e.id}/eligibility`, { eligible: !(e.eligible ?? true) }).then(load).catch((x) => toast.error(errMsg(x)));
  if (!d) return null;
  return (
    <div data-testid="admin-giveaway">
      <PageHead title="Giveaway Entries" />
      <div className="grid sm:grid-cols-3 gap-3">
        <Stat k="Total entries" v={d.total} testid="giveaway-total" />
        <Stat k="Left to draw from" v={d.eligible} testid="giveaway-eligible" />
        <Stat k="Marketing opt-ins" v={d.marketing_opt_ins} testid="giveaway-optins" />
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        <button onClick={download} data-testid="giveaway-export-button" className="btn-ghost !py-3 !px-5 text-sm"><Download size={16} />Download CSV (Excel)</button>
        <button onClick={draw} data-testid="giveaway-draw-button" className="btn-primary !py-3 !px-5 text-sm"><Shuffle size={16} />Draw random winner</button>
      </div>
      <Winners draws={d.draws} />
      <div className="mt-6">
        <Table head={["Entered", "Email", "Promotions opt-in", "Eligible"]} testid="giveaway-table">
          {d.entries.map((e) => (
            <tr key={e.id} className="border-b border-white/5" data-testid={`giveaway-row-${e.id}`}>
              <td className="px-4 py-3 text-slate-400 whitespace-nowrap">{fmtDate(e.created_at)}</td>
              <td className="px-4 py-3">{e.email}</td>
              <td className="px-4 py-3">{e.marketing_opt_in ? "Yes" : "No"}</td>
              <td className="px-4 py-3">
                <button onClick={() => toggle(e)} data-testid={`giveaway-eligible-toggle-${e.id}`} className={`text-xs rounded-full px-3 py-1 ${e.eligible === false ? "bg-red-400/15 text-red-300" : "bg-[var(--accent)]/15 accent-text"}`}>
                  {e.eligible === false ? "Excluded" : "Eligible"}
                </button>
                {e.winner && <span data-testid={`giveaway-winner-badge-${e.id}`} className="ml-2 inline-flex items-center gap-1 text-xs rounded-full px-3 py-1 bg-yellow-300/15 text-yellow-200"><Trophy size={12} />Winner</span>}
              </td>
            </tr>
          ))}
        </Table>
        {d.entries.length === 0 && <p data-testid="giveaway-empty" className="mt-4 text-sm text-slate-400">No entries yet.</p>}
      </div>
    </div>
  );
}
